/**
 * Filtros compartilhados de /relatorios.
 *
 * A mesma semântica é usada pelas queries do PostgREST e pelos caches offline.
 * As funções deste arquivo são o lado offline/testável; os hooks traduzem o
 * mesmo estado para filtros positivos no servidor ANTES de `.range()`.
 */
import type { FinanceEntryWithStatus } from '@/types/database';
import { effectiveStatus, type IsoDateRange, type PaymentDateBasis } from '@/lib/paymentsReport';
import { receiptState, type ReceiptState } from '@/lib/documentsReport';

export type ReportTab = 'quotes' | 'items' | 'payments' | 'stock' | 'documents';

export type ReportDocumentType = 'service_order' | 'contract' | 'receipt';

export interface DocumentStatusFilter {
  type: ReportDocumentType;
  status: string;
}

export interface ReportFilterState {
  /** Compartilhado entre Orçamentos, Itens, Pagamentos e Documentos. */
  clientId: string | null;
  /** Compartilhado entre Orçamentos, Itens e Estoque. */
  productId: string | null;
  /** Vocabulário independente por aba. Documentos usa um token contextual. */
  statusByTab: Record<ReportTab, string | null>;
}

export const EMPTY_REPORT_FILTERS: ReportFilterState = {
  clientId: null,
  productId: null,
  statusByTab: {
    quotes: null,
    items: null,
    payments: null,
    stock: null,
    documents: null,
  },
};

export const REPORT_FILTER_APPLICABILITY = {
  quotes: { client: true, product: true, status: true },
  items: { client: true, product: true, status: true },
  payments: { client: true, product: false, status: true },
  stock: { client: false, product: true, status: false },
  documents: { client: true, product: false, status: true },
} as const satisfies Record<
  ReportTab,
  { client: boolean; product: boolean; status: boolean }
>;

export function documentStatusToken(filter: DocumentStatusFilter): string {
  return `${filter.type}:${filter.status}`;
}

export function parseDocumentStatusToken(raw: string | null | undefined): DocumentStatusFilter | null {
  if (!raw) return null;
  const separator = raw.indexOf(':');
  if (separator <= 0 || separator === raw.length - 1) return null;
  const type = raw.slice(0, separator) as ReportDocumentType;
  if (!['service_order', 'contract', 'receipt'].includes(type)) return null;
  return { type, status: raw.slice(separator + 1) };
}

export function activeFiltersForTab(
  filters: ReportFilterState,
  tab: ReportTab,
): { clientId: string | null; productId: string | null; status: string | null } {
  const applies = REPORT_FILTER_APPLICABILITY[tab];
  return {
    clientId: applies.client ? filters.clientId : null,
    productId: applies.product ? filters.productId : null,
    status: applies.status ? filters.statusByTab[tab] : null,
  };
}

export function activeFilterCount(filters: ReportFilterState, tab: ReportTab): number {
  const active = activeFiltersForTab(filters, tab);
  return Number(Boolean(active.clientId))
    + Number(Boolean(active.productId))
    + Number(Boolean(active.status));
}

export function clearFiltersForTab(
  filters: ReportFilterState,
  tab: ReportTab,
): ReportFilterState {
  const applies = REPORT_FILTER_APPLICABILITY[tab];
  return {
    ...filters,
    clientId: applies.client ? null : filters.clientId,
    productId: applies.product ? null : filters.productId,
    statusByTab: {
      ...filters.statusByTab,
      [tab]: applies.status ? null : filters.statusByTab[tab],
    },
  };
}

function nestedItems(row: any): readonly any[] {
  if (Array.isArray(row?.quote_items)) return row.quote_items;
  if (Array.isArray(row?._offline_items)) return row._offline_items;
  if (Array.isArray(row?.items)) return row.items;
  return [];
}

/** Mesma seleção de linhas usada pelo embed filtrado `quote_items!inner`. */
export function reportQuoteItems(row: any, productId: string | null): readonly any[] {
  const items = nestedItems(row);
  return productId
    ? items.filter((item) => item?.product_id === productId)
    : items;
}

/** Predicado POSITIVO: `document_type` nulo continua sendo orçamento legado. */
export function isReportQuote(row: any): boolean {
  return row?.document_type !== 'pedido';
}

export function quoteMatchesReportFilters(
  row: any,
  filters: Pick<ReportFilterState, 'clientId' | 'productId' | 'statusByTab'>,
  tab: 'quotes' | 'items',
): boolean {
  const active = activeFiltersForTab(filters as ReportFilterState, tab);
  if (active.clientId && row?.client_id !== active.clientId) return false;
  if (active.status && row?.status !== active.status) return false;
  if (
    active.productId
    && reportQuoteItems(row, active.productId).length === 0
  ) {
    return false;
  }
  return true;
}

export function stockUnitMatchesReportFilters(row: any, filters: ReportFilterState): boolean {
  const active = activeFiltersForTab(filters, 'stock');
  return !active.productId || row?.id === active.productId;
}

function inIsoRange(value: string | null | undefined, range: IsoDateRange): boolean {
  if (!value) return false;
  return value >= range.from && value <= range.to;
}

/**
 * A aba Pagamentos precisa da UNIÃO de duas janelas na base realizada:
 * pagamento no período (realizado) OU vencimento no período (abertos/vencidos).
 */
export function financeEntryInReportWindow(
  row: FinanceEntryWithStatus,
  range: IsoDateRange,
  basis: PaymentDateBasis,
): boolean {
  if (basis === 'due') return inIsoRange(row.due_date, range);
  return inIsoRange(row.paid_date, range) || inIsoRange(row.due_date, range);
}

export function financeEntryMatchesReportFilters(
  row: FinanceEntryWithStatus,
  range: IsoDateRange,
  basis: PaymentDateBasis,
  filters: ReportFilterState,
): boolean {
  if (!financeEntryInReportWindow(row, range, basis)) return false;
  const active = activeFiltersForTab(filters, 'payments');
  if (active.clientId && row.client_id !== active.clientId) return false;
  if (active.status && effectiveStatus(row) !== active.status) return false;
  return true;
}

export function serviceOrderMatchesReportFilters(row: any, filters: ReportFilterState): boolean {
  const active = activeFiltersForTab(filters, 'documents');
  if (active.clientId && row?.client_id !== active.clientId) return false;
  const contextual = parseDocumentStatusToken(active.status);
  if (!contextual) return true;
  return contextual.type === 'service_order' && row?.status === contextual.status;
}

export function contractMatchesReportFilters(row: any, filters: ReportFilterState): boolean {
  const active = activeFiltersForTab(filters, 'documents');
  if (active.clientId && row?.client_id !== active.clientId) return false;
  const contextual = parseDocumentStatusToken(active.status);
  if (!contextual) return true;
  return contextual.type === 'contract' && row?.status === contextual.status;
}

export function receiptMatchesReportFilters(row: any, filters: ReportFilterState): boolean {
  const active = activeFiltersForTab(filters, 'documents');
  if (active.clientId && row?.client_id !== active.clientId) return false;
  const contextual = parseDocumentStatusToken(active.status);
  if (!contextual) return true;
  const state: ReceiptState = receiptState({ cancelledAt: row?.cancelled_at ?? null });
  return contextual.type === 'receipt' && state === contextual.status;
}
